import { ref, computed, watch, nextTick } from "vue";
import { g as flatten_1, e as isString_1, f as isFunction_1 } from "../../vendor-b303cd6e.mjs";
import { u as useSyncProp } from "./useSyncProp-326abeda.mjs";
import { u as useFocus } from "./useFocus-4393f76a.mjs";
import { u as useFormChild } from "./useForm/useFormChild-28e78c0e.mjs";
const normalizeValidationRules = (rules = [], callArguments = null) => {
  if (isString_1(rules)) {
    rules = [rules];
  }
  return rules.map((rule) => isFunction_1(rule) ? rule(callArguments) : rule);
};
const useValidationProps = {
  name: { type: String, default: void 0 },
  modelValue: { required: false },
  error: { type: Boolean, default: void 0 },
  errorMessages: { type: [Array, String], default: void 0 },
  errorCount: { type: [String, Number], default: 1 },
  rules: { type: Array, default: () => [] },
  success: { type: Boolean, default: false },
  messages: { type: [Array, String], default: () => [] },
  immediateValidation: { type: Boolean, default: false }
};
const useValidationEmits = ["update:error", "update:errorMessages"];
const isPromise = (value) => {
  return typeof value === "object" && typeof value.then === "function";
};
const useValidation = (props, emit, options) => {
  const { reset, focus } = options;
  const { isFocused, onFocus, onBlur } = useFocus();
  const [computedError] = useSyncProp("error", props, emit, false);
  const [computedErrorMessages] = useSyncProp("errorMessages", props, emit, []);
  const isLoading = ref(false);
  const validationAriaAttributes = computed(() => ({
    "aria-invalid": !!computedErrorMessages.value.length,
    "aria-errormessage": typeof computedErrorMessages.value === "string" ? computedErrorMessages.value : computedErrorMessages.value.join(", ")
  }));
  const resetValidation = () => {
    computedError.value = false;
    computedErrorMessages.value = [];
  };
  const processResults = (results) => {
    let error = false;
    let errorMessages = [];
    results.forEach((result) => {
      if (isString_1(result)) {
        errorMessages = [...errorMessages, result];
        error = true;
      } else if (result === false) {
        error = true;
      }
    });
    computedErrorMessages.value = errorMessages;
    computedError.value = error;
    return !error;
  };
  const validateAsync = async () => {
    if (!props.rules || !props.rules.length) {
      return true;
    }
    const results = normalizeValidationRules(flatten_1(props.rules), options.value.value);
    const asyncPromiseResults = results.filter((result) => isPromise(result));
    const syncRules = results.filter((result) => !isPromise(result));
    if (!asyncPromiseResults.length) {
      return processResults(syncRules);
    }
    isLoading.value = true;
    return Promise.all(asyncPromiseResults).then((asyncResults) => {
      isLoading.value = false;
      return processResults([...syncRules, ...asyncResults]);
    });
  };
  const validate = () => {
    if (!props.rules || !props.rules.length) {
      return true;
    }
    const rules = flatten_1(props.rules);
    const results = normalizeValidationRules(rules, options.value.value);
    const asyncPromiseResults = results.filter((result) => isPromise(result));
    const syncRules = results.filter((result) => !isPromise(result));
    const isSyncedError = syncRules.some((result) => isString_1(result) ? result : result === false);
    if (asyncPromiseResults.length && !isSyncedError) {
      isLoading.value = true;
      Promise.all(asyncPromiseResults).then((asyncResults) => {
        processResults([...syncRules, ...asyncResults]);
        isLoading.value = false;
      });
      return isSyncedError;
    }
    return processResults(syncRules);
  };
  watch(isFocused, (newVal) => !newVal && validate());
  let canValidate = true;
  const withoutValidation = (cb) => {
    canValidate = false;
    cb();
    nextTick(() => {
      canValidate = true;
    });
  };
  watch(
    () => props.modelValue,
    () => {
      if (!canValidate) {
        return;
      }
      return validate();
    },
    { immediate: props.immediateValidation }
  );
  const {
    doShowErrorMessages,
    doShowError,
    doShowLoading
  } = useFormChild(() => ({
    isValid: !computedError.value,
    isLoading: isLoading.value,
    errorMessages: computedErrorMessages.value,
    validate,
    validateAsync,
    resetValidation,
    focus,
    reset,
    value: options.value || props.modelValue,
    name: props.name
  }));
  return {
    computedError: computed(() => doShowError.value ? computedError.value : false),
    computedErrorMessages: computed(() => doShowErrorMessages.value ? computedErrorMessages.value : []),
    isLoading: computed(() => doShowLoading.value ? isLoading.value : false),
    listeners: { onFocus, onBlur },
    validate,
    resetValidation,
    withoutValidation,
    validationAriaAttributes
  };
};
export {
  useValidationEmits as a,
  useValidation as b,
  useValidationProps as u
};
//# sourceMappingURL=useValidation-a080a9b3.mjs.map
